import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import BrandMark from '../components/BrandMark';

const P  = "'JetBrains Mono', monospace";
const D  = "'Archivo', sans-serif";
const C  = '#00DBE9';   // --accent — Signal Cyan
const G  = '#ABD600';   // --accent-2 — Race Lime (driver accent)
const BG = '#04070A';   // --surface-base

// ── CSS injected once ─────────────────────────────────────────────────────────
const CSS = `
  @keyframes glitch-a {
    0%,100%{ clip-path:inset(40% 0 50% 0); transform:translate(-3px,0); }
    33%    { clip-path:inset(10% 0 75% 0); transform:translate( 3px,0); }
    66%    { clip-path:inset(70% 0 10% 0); transform:translate(-2px,0); }
  }
  @keyframes glitch-b {
    0%,100%{ clip-path:inset(55% 0 30% 0); transform:translate( 3px,0); }
    33%    { clip-path:inset( 5% 0 85% 0); transform:translate(-3px,0); }
    66%    { clip-path:inset(80% 0  5% 0); transform:translate( 2px,0); }
  }
  @keyframes scan  { from{ top:-5% } to{ top:110% } }
  @keyframes blink { 0%,49%{ opacity:1 } 50%,100%{ opacity:0 } }
  @keyframes flicker { 0%,97%,100%{ opacity:1 } 98%{ opacity:.82 } }
  .glitch-wrap { position:relative; display:inline-block; animation:flicker 7s infinite; }
  .glitch-wrap::before,.glitch-wrap::after {
    content:attr(data-text); position:absolute; top:0; left:0;
    width:100%; height:100%;
    font-family:inherit; font-size:inherit; font-weight:inherit;
    font-style:inherit; color:inherit; letter-spacing:inherit; text-transform:inherit;
  }
  .glitch-wrap::before { color:#ff004c; animation:glitch-a 3.2s infinite; opacity:.6; }
  .glitch-wrap::after  { color:#00d4ff; animation:glitch-b 2.7s infinite; opacity:.6; }
  .auth-in { transition:border-color .15s; }
  .auth-in:focus { outline:none; border-left-color:var(--ac) !important; border-bottom-color:var(--ac) !important; }
  .auth-in::placeholder { color:rgba(132,148,149,.3); }
  .role-btn { transition:all .18s; }
  .role-btn:hover { filter:brightness(1.15); }
  .sub-btn  { transition:box-shadow .2s; }
  .sub-btn:hover:not(:disabled) { filter:brightness(1.1); }
  @media(max-width:480px){ .auth-card{ padding:24px 20px 20px !important; } }
`;

// ── Glitch logo ───────────────────────────────────────────────────────────────
function Glitch({ text, color, size = '2rem' }) {
  return (
    <span className="glitch-wrap" data-text={text}
      style={{ fontFamily: D, fontWeight: 900, fontStyle: 'italic', fontSize: size, letterSpacing: '0.08em', color, textShadow: `0 0 28px ${color}77` }}>
      {text}
    </span>
  );
}

// ── Boot ticker ───────────────────────────────────────────────────────────────
function Boot() {
  const lines = ['> CONNECTING TO RACE SERVER...', '> AUTHENTICATING NODE...', '> LOADING CRIT DATABASE...', '> READY.'];
  const [n, setN] = useState(0);
  useEffect(() => {
    if (n >= lines.length) return;
    const t = setTimeout(() => setN(x => x + 1), 320 + Math.random() * 180);
    return () => clearTimeout(t);
  }, [n]); // eslint-disable-line
  return (
    <div style={{ fontFamily: P, fontSize: 9, color: `${C}55`, letterSpacing: '0.1em', lineHeight: 1.9, minHeight: 64, textAlign: 'left', display: 'inline-block', marginTop: 8 }}>
      {lines.slice(0, n).map((l, i) => (
        <div key={i} style={{ color: i === n - 1 ? `${C}99` : `${C}33` }}>{l}</div>
      ))}
      {n < lines.length && <span style={{ display: 'inline-block', width: 7, height: 12, background: C, verticalAlign: 'middle', animation: 'blink .8s infinite', marginLeft: 2 }} />}
    </div>
  );
}

// ── Input ─────────────────────────────────────────────────────────────────────
function Field({ label, type = 'text', value, onChange, placeholder, accent = C, autoComplete }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ fontFamily: P, fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', color: `${accent}ee`, marginBottom: 9 }}>{label}</div>
      <input className="auth-in" type={type} value={value} onChange={onChange}
        placeholder={placeholder} autoComplete={autoComplete}
        style={{ '--ac': accent, display: 'block', width: '100%', boxSizing: 'border-box', background: `${accent}07`, border: 'none', borderBottom: `1px solid ${accent}44`, borderLeft: `2px solid ${accent}33`, padding: '11px 12px', fontFamily: P, fontSize: 14, color: '#e8f4f4', letterSpacing: '0.05em' }}
      />
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function Login({ onLogin }) {
  const [role, setRole]   = useState(null);          // null | 'manager' | 'driver'
  const [mode, setMode]   = useState('login');        // 'login' | 'signup'
  const [name, setName]   = useState('');
  const [email, setEmail] = useState('');
  const [pw, setPw]       = useState('');
  const [err, setErr]     = useState('');
  const [busy, setBusy]   = useState(false);
  const navigate = useNavigate();

  const accent = role === 'driver' ? G : C;

  const reset = (r) => { setRole(r); setMode('login'); setErr(''); setName(''); setEmail(''); setPw(''); };

  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      if (mode === 'signup') {
        if (role === 'manager') {
          await axios.post('/api/auth/register', { name, email, password: pw });
        } else {
          await axios.post('/api/auth/driver-register', { name, email, password: pw });
        }
      }
      const loginUrl = role === 'driver' ? '/api/auth/driver-login' : '/api/auth/login';
      const res = await axios.post(loginUrl, { email, password: pw });
      onLogin(res.data.token, res.data.user);
      if (mode === 'signup') navigate('/verify-email');
    } catch (e) {
      const code = e.response?.data?.error;
      const map = {
        DRIVER_ACCOUNT_EXISTS: 'FAHRERKONTO EXISTIERT — NUTZE DEN FAHRER-LOGIN.',
        TEAM_ACCOUNT_EXISTS:   'TEAMKONTO EXISTIERT — NUTZE DEN TEAM MANAGER LOGIN.',
        INVALID_CREDENTIALS:   'FALSCHE E-MAIL ODER PASSWORT.',
        USER_EXISTS:           'E-MAIL BEREITS VERGEBEN.',
      };
      setErr(map[code] || code || 'FEHLER — BITTE NOCHMAL VERSUCHEN.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: BG, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px', position: 'relative', overflow: 'hidden' }}>
      <style>{CSS}</style>

      {/* BG layers */}
      <div style={{ position: 'fixed', inset: 0, backgroundImage: `linear-gradient(${C}04 1px,transparent 1px),linear-gradient(90deg,${C}04 1px,transparent 1px)`, backgroundSize: '44px 44px', pointerEvents: 'none', zIndex: 0 }} />
      <div style={{ position: 'fixed', inset: 0, background: `radial-gradient(ellipse at 50% 50%,${accent}07 0%,transparent 65%)`, pointerEvents: 'none', zIndex: 0, transition: 'background 0.4s' }} />
      {/* Scanline */}
      <div style={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
        <div style={{ position: 'absolute', left: 0, right: 0, height: 2, background: `linear-gradient(transparent,${C}14,transparent)`, animation: 'scan 5s linear infinite' }} />
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'repeating-linear-gradient(0deg,transparent,transparent 3px,rgba(0,0,0,.07) 3px,rgba(0,0,0,.07) 4px)' }} />
      </div>
      {/* Corner brackets */}
      {[['top','left'],['top','right'],['bottom','left'],['bottom','right']].map(([v,h]) => (
        <div key={v+h} style={{ position:'fixed', [v]:16, [h]:16, width:48, height:48, [`border${v[0].toUpperCase()+v.slice(1)}`]:`2px solid ${accent}44`, [`border${h[0].toUpperCase()+h.slice(1)}`]:`2px solid ${accent}44`, pointerEvents:'none', zIndex:1, transition:'border-color .4s' }} />
      ))}

      {/* Back */}
      <div style={{ position:'fixed', top:16, left:20, zIndex:10 }}>
        <Link to="/" style={{ fontFamily:P, fontSize:11, fontWeight:700, letterSpacing:'0.12em', color:'rgba(185,210,210,.6)', textDecoration:'none', display:'flex', alignItems:'center', gap:8, padding:'8px 12px', border:'1px solid rgba(59,73,75,.3)', background:'rgba(8,11,14,.7)', backdropFilter:'blur(8px)' }}
          onMouseEnter={e=>e.currentTarget.style.color=accent} onMouseLeave={e=>e.currentTarget.style.color='rgba(185,210,210,.6)'}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" strokeLinejoin="miter"><polyline points="15,18 9,12 15,6"/></svg>
          ZURÜCK
        </Link>
      </div>

      {/* Card */}
      <div style={{ position:'relative', zIndex:2, width:'100%', maxWidth:440 }}>

        {/* Logo */}
        <div style={{ textAlign:'center', marginBottom:28 }}>
          <div style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:50, height:50, border:`1px solid ${C}44`, background:`${C}08`, marginBottom:14, boxShadow:`0 0 24px ${C}22` }}>
            <BrandMark size={34} variant="cyan" />
          </div>
          <div><Glitch text="CRIT ROSTER" color={C} size="clamp(26px,5vw,36px)" /></div>
          <Boot />
        </div>

        {/* ── STEP 1: Role picker ── */}
        {!role && (
          <div>
            <div style={{ fontFamily:P, fontSize:10, fontWeight:700, letterSpacing:'0.2em', color:'rgba(132,148,149,.5)', textAlign:'center', marginBottom:20 }}>
              ICH BIN…
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              {[
                { key:'manager', icon:'MGR', label:'TEAM\nMANAGER', sub:'Du verwaltest ein Team', color:C },
                { key:'driver',  icon:'DRV', label:'FAHRER',        sub:'Du bist im Team',      color:G },
              ].map(r => (
                <button key={r.key} className="role-btn" onClick={() => reset(r.key)} style={{
                  background:`${r.color}08`, border:`1px solid ${r.color}33`,
                  padding:'28px 16px', cursor:'pointer', textAlign:'center', display:'flex', flexDirection:'column', alignItems:'center', gap:12,
                }}>
                  <span style={{ fontFamily:P, fontSize:11, fontWeight:700, letterSpacing:'0.18em', color:`${r.color}99` }}>{r.icon}</span>
                  <div style={{ fontFamily:D, fontWeight:900, fontStyle:'italic', fontSize:16, color:r.color, textTransform:'uppercase', letterSpacing:'0.06em', whiteSpace:'pre-line', lineHeight:1.1 }}>{r.label}</div>
                  <div style={{ fontFamily:P, fontSize:10, color:'rgba(185,202,203,.55)', letterSpacing:'0.06em' }}>{r.sub}</div>
                </button>
              ))}
            </div>
            {/* Forgot password corner */}
            <div style={{ marginTop:20, textAlign:'center' }}>
              <Link to="/forgot-password" style={{ fontFamily:P, fontSize:9, color:'rgba(132,148,149,.35)', textDecoration:'none', letterSpacing:'0.1em' }}
                onMouseEnter={e=>e.target.style.color=C} onMouseLeave={e=>e.target.style.color='rgba(132,148,149,.35)'}>
                PASSWORT VERGESSEN?
              </Link>
            </div>
          </div>
        )}

        {/* ── STEP 2: Form ── */}
        {role && (
          <div>
            {/* Role indicator + change */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16, padding:'10px 14px', background:`${accent}08`, border:`1px solid ${accent}22` }}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ fontFamily:P, fontSize:9, fontWeight:700, letterSpacing:'0.14em', color:`${accent}88` }}>{role === 'manager' ? 'MGR' : 'DRV'}</span>
                <div>
                  <div style={{ fontFamily:D, fontWeight:800, fontStyle:'italic', fontSize:13, color:accent, textTransform:'uppercase', letterSpacing:'0.06em' }}>
                    {role === 'manager' ? 'Team Manager' : 'Fahrer'}
                  </div>
                  <div style={{ fontFamily:P, fontSize:9, color:'rgba(132,148,149,.5)', letterSpacing:'0.08em', marginTop:1 }}>
                    {role === 'manager' ? 'Teamverwaltung & Events' : 'Teilnahme & Rennen'}
                  </div>
                </div>
              </div>
              <button onClick={() => setRole(null)} style={{ fontFamily:P, fontSize:9, fontWeight:700, letterSpacing:'0.1em', background:'transparent', border:`1px solid rgba(132,148,149,.25)`, color:'rgba(132,148,149,.5)', padding:'5px 10px', cursor:'pointer' }}
                onMouseEnter={e=>{ e.currentTarget.style.borderColor=`${accent}55`; e.currentTarget.style.color=accent; }}
                onMouseLeave={e=>{ e.currentTarget.style.borderColor='rgba(132,148,149,.25)'; e.currentTarget.style.color='rgba(132,148,149,.5)'; }}>
                ÄNDERN
              </button>
            </div>

            {/* Login / Signup tabs */}
            <div style={{ display:'flex', marginBottom:0 }}>
              {[['login','LOGIN'],['signup','SIGN UP']].map(([k,l]) => (
                <button key={k} onClick={() => { setMode(k); setErr(''); }} style={{
                  flex:1, padding:'12px 0', fontFamily:P, fontSize:11, fontWeight:700, letterSpacing:'0.16em',
                  background: mode===k ? `${accent}0a` : 'transparent', border:'none',
                  borderBottom: mode===k ? `2px solid ${accent}` : '2px solid rgba(59,73,75,.3)',
                  color: mode===k ? accent : 'rgba(132,148,149,.4)', cursor:'pointer', transition:'all .15s',
                }}>{l}</button>
              ))}
            </div>

            {/* Form card */}
            <div className="auth-card" style={{ background:'rgba(10,14,16,.97)', border:`1px solid ${accent}18`, borderTop:'none', padding:'28px 28px 24px', boxShadow:`0 0 60px rgba(0,0,0,.55), inset 0 0 40px ${accent}06` }}>
              {err && (
                <div style={{ marginBottom:20, padding:'10px 14px', background:'rgba(255,0,76,.07)', border:'1px solid rgba(255,0,76,.35)', fontFamily:P, fontSize:10, color:'#ff4d7a', letterSpacing:'0.08em', lineHeight:1.5 }}>
                  ⚡ {err}
                </div>
              )}
              <form onSubmit={submit}>
                {mode === 'signup' && (
                  <Field
                    label={role === 'manager' ? 'TEAM NAME' : 'DEIN NAME'}
                    value={name} onChange={e=>setName(e.target.value)}
                    placeholder={role === 'manager' ? 'z.B. STURMVOGEL RACING' : 'Max Mustermann'}
                    accent={accent} autoComplete={role === 'manager' ? 'organization' : 'name'}
                  />
                )}
                <Field label="E-MAIL" type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="name@email.com" accent={accent} autoComplete="email" />
                <Field label="PASSWORT" type="password" value={pw} onChange={e=>setPw(e.target.value)} placeholder="••••••••••••" accent={accent} autoComplete={mode==='login' ? 'current-password' : 'new-password'} />

                {mode === 'login' && (
                  <div style={{ marginBottom:20, marginTop:-10, textAlign:'right' }}>
                    <Link to="/forgot-password" style={{ fontFamily:P, fontSize:9, color:'rgba(132,148,149,.35)', textDecoration:'none', letterSpacing:'0.1em' }}
                      onMouseEnter={e=>e.target.style.color=accent} onMouseLeave={e=>e.target.style.color='rgba(132,148,149,.35)'}>
                      PASSWORT VERGESSEN?
                    </Link>
                  </div>
                )}

                <button type="submit" disabled={busy} className="sub-btn" style={{
                  width:'100%', padding:'15px 0',
                  background: busy ? `${accent}66` : accent,
                  border:'none', cursor: busy ? 'not-allowed' : 'pointer',
                  fontFamily:P, fontSize:12, fontWeight:700, letterSpacing:'0.2em',
                  color: role==='driver' ? '#001a0d' : '#002022',
                  boxShadow: busy ? 'none' : `0 0 24px ${accent}44`, transition:'all .2s',
                }}>
                  {busy
                    ? '[ CONNECTING... ]'
                    : mode === 'login'
                      ? '[ ACCESS SYSTEM ]'
                      : role === 'manager' ? '[ CREATE TEAM ]' : '[ JOIN THE GRID ]'
                  }
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 4px 0', fontFamily:P, fontSize:8, color:`${C}30`, letterSpacing:'0.1em' }}>
          <span>SYS_AUTH_V2.4.1</span>
          <div style={{ display:'flex', gap:16 }}>
            <Link to="/impressum" style={{ fontFamily:P, fontSize:8, color:'rgba(132,148,149,.35)', textDecoration:'none', letterSpacing:'0.1em' }}
              onMouseEnter={e=>e.target.style.color=C} onMouseLeave={e=>e.target.style.color='rgba(132,148,149,.35)'}>
              IMPRESSUM
            </Link>
            <Link to="/datenschutz" style={{ fontFamily:P, fontSize:8, color:'rgba(132,148,149,.35)', textDecoration:'none', letterSpacing:'0.1em' }}
              onMouseEnter={e=>e.target.style.color=C} onMouseLeave={e=>e.target.style.color='rgba(132,148,149,.35)'}>
              DATENSCHUTZ
            </Link>
          </div>
          <span>© 2026</span>
        </div>
      </div>
    </div>
  );
}
